import { useState, useEffect, useCallback, useRef } from "react";
import { useApiError } from "./useApiError";
import { BitmakerAPIClient } from "../services/BitmakerApp";
import {
  SprintUserIssue,
  SprintUserIssues,
  SprintUserIssuesFromJSON,
} from "../bitmaker/schemas/Sprints/SprintUserIssues";

type CombinedIssue = SprintUserIssue & {
  isReviewer?: boolean;
  isInReview?: boolean;
};

export const useSprintUserIssues = (login?: string, sprintId?: string) => {
  const [issuesData, setIssuesData] = useState<SprintUserIssues | null>(null);
  const [loadingSprintIssues, setLoadingSprintIssues] = useState(false);
  const [fetchSprintIssuesError, setFetchSprintIssuesError] = useState<
    string | null
  >(null);

  const { handleAPIError } = useApiError();
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchUserSprintIssues = useCallback(() => {
    if (!isMountedRef.current || !login) return;

    setLoadingSprintIssues(true);
    setFetchSprintIssuesError(null);

    const url = sprintId
      ? `/sprints/${sprintId}/users/${login}/issues`
      : `/sprints/current/users/${login}/issues`;

    BitmakerAPIClient.get(url, null, (response) => {
      if (!isMountedRef.current) return;

      const { isError, message } = handleAPIError(response);

      if (isError) {
        console.error("Error fetching user sprint issues:", message);
        setFetchSprintIssuesError(
          "Error fetching user sprint issues. Please try again.",
        );
        setLoadingSprintIssues(false);
        return;
      }

      setIssuesData(
        response.data ? SprintUserIssuesFromJSON(response.data) : null,
      );
      setLoadingSprintIssues(false);
    });
  }, [login, sprintId, handleAPIError]);

  useEffect(() => {
    fetchUserSprintIssues();
  }, [fetchUserSprintIssues]);

  const combinedIssues: CombinedIssue[] = issuesData
    ? [
        ...issuesData.inReviewIssues.map((i) => ({
          ...i,
          isReviewer: true,
          isInReview: true,
        })),
        ...issuesData.spilloverIssues,
        ...issuesData.issues,
        ...issuesData.reviewer.map((i) => ({
          ...i,
          isReviewer: true,
        })),
        ...issuesData.doneIssues,
        ...issuesData.doneReviewer.map((i) => ({
          ...i,
          isReviewer: true,
        })),
      ]
    : [];

  return {
    sprintIssues: issuesData,
    combinedIssues,
    loadingSprintIssues,
    fetchSprintIssuesError,
    refetchSprintIssues: fetchUserSprintIssues,
  };
};
